import { Fragment, useMemo, useState } from 'react';
import { CalendarRange } from 'lucide-react';
import type { AbsenceCalendarRow } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  ABSENCE_TYPE_META, absenceStatusLabel, absenceTypeLabel, absenceTypeShort,
  eachDay, formatDateRange, formatMonthYear, isWeekend, todayYmd, weekdayLetters,
} from '@/lib/hr';
import { t } from '@/i18n';

const PAGE_SIZE = 30;

interface TeamCalendarGridProps {
  rows: AbsenceCalendarRow[];
  from: string;
  to: string;
  /** Map<YYYY-MM-DD, holiday name>. */
  holidays: Map<string, string>;
  /** HR/admin callers see reason + sick-leave letter code in the hover card. */
  isHr: boolean;
  loading?: boolean;
}

/**
 * People × days absence grid: sticky name column, weekend/holiday columns
 * shaded, today marked, absences as type-coloured bands spanning their range
 * (dashed = pending) with a hover/focus card. Rows paginate past 30 people so
 * a 100-person company stays scrollable and fast.
 */
export function TeamCalendarGrid({ rows, from, to, holidays, isHr, loading }: TeamCalendarGridProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const today = todayYmd();

  const days = useMemo(() => eachDay(from, to), [from, to]);
  const dayIndex = useMemo(() => new Map(days.map((d, i) => [d, i])), [days]);
  const compact = days.length > 40;

  const template = `minmax(9rem, 12rem) repeat(${days.length}, minmax(${compact ? 1.05 : 1.75}rem, 1fr))`;

  // Month bands for the top header row (one per calendar month in range).
  const monthBands = useMemo(() => {
    const bands: Array<{ label: string; startIdx: number; span: number }> = [];
    for (let i = 0; i < days.length; i += 1) {
      const key = days[i].slice(0, 7);
      const last = bands[bands.length - 1];
      if (last && days[last.startIdx].slice(0, 7) === key) last.span += 1;
      else bands.push({ label: formatMonthYear(days[i]), startIdx: i, span: 1 });
    }
    return bands;
  }, [days]);

  const legendTypes = useMemo(
    () => [...new Set(rows.flatMap((r) => r.entries.map((e) => e.type)))],
    [rows],
  );

  const letters = weekdayLetters();
  const offDay = (d: string) => isWeekend(d) || holidays.has(d);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-9 w-full" />)}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-center text-sm">
        <CalendarRange className="size-8 opacity-40" />
        <p>{t('hr.grid.empty')}</p>
      </div>
    );
  }

  const visibleRows = rows.slice(0, visibleCount);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="overflow-x-auto rounded-lg border">
        <div className="min-w-max">
          {/* Header: month band(s) */}
          <div className="grid border-b" style={{ gridTemplateColumns: template }}>
            <div className="bg-card sticky left-0 z-20 border-r" />
            {monthBands.map((band, i) => (
              <div
                key={band.label + band.startIdx}
                className={cn(
                  'text-muted-foreground px-2 py-1 text-xs font-medium capitalize',
                  i > 0 && 'border-l',
                )}
                style={{ gridColumn: `${band.startIdx + 2} / span ${band.span}` }}
              >
                {band.label}
              </div>
            ))}
          </div>

          {/* Header: day numbers */}
          <div className="grid border-b" style={{ gridTemplateColumns: template }}>
            <div className="bg-card text-muted-foreground sticky left-0 z-20 border-r px-2 py-1 text-xs">
              {t('hr.grid.people', { count: rows.length })}
            </div>
            {days.map((d, j) => {
              const holidayName = holidays.get(d);
              const isToday = d === today;
              return (
                <div
                  key={d}
                  title={holidayName}
                  className={cn(
                    'flex flex-col items-center justify-center py-0.5 text-center',
                    offDay(d) && 'bg-muted/60',
                  )}
                  style={{ gridColumn: j + 2 }}
                >
                  {!compact && (
                    <span className="text-muted-foreground text-[9px] leading-none">
                      {letters[(new Date(`${d}T00:00:00Z`).getUTCDay() + 6) % 7]}
                    </span>
                  )}
                  <span
                    className={cn(
                      'text-[10px] leading-4 tabular-nums',
                      offDay(d) ? 'text-muted-foreground' : 'text-foreground',
                      isToday && 'bg-primary text-primary-foreground flex size-4 items-center justify-center rounded-full font-semibold',
                    )}
                  >
                    {parseInt(d.slice(8), 10)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Body rows */}
          {visibleRows.map((row) => (
            <div
              key={row.employeeId}
              className="group grid border-b last:border-b-0"
              style={{ gridTemplateColumns: template }}
            >
              <div
                className="bg-card group-hover:bg-accent/50 sticky left-0 z-20 flex min-w-0 flex-col justify-center border-r px-2 py-1"
                style={{ gridColumn: 1, gridRow: 1 }}
              >
                <span className="truncate text-xs font-medium">
                  {row.firstName} {row.lastName}
                </span>
                {row.department && (
                  <span className="text-muted-foreground truncate text-[10px]">{row.department}</span>
                )}
              </div>

              {days.map((d, j) => (
                <div
                  key={d}
                  className={cn(
                    'min-h-9', // stretches with the row when the name cell is taller
                    j > 0 && 'border-l',
                    offDay(d) && 'bg-muted/60',
                    d === today && 'bg-primary/5',
                  )}
                  style={{ gridColumn: j + 2, gridRow: 1 }}
                />
              ))}

              {row.entries.map((entry) => {
                const clampedFrom = entry.dateFrom < from ? from : entry.dateFrom;
                const clampedTo = entry.dateTo > to ? to : entry.dateTo;
                const sIdx = dayIndex.get(clampedFrom);
                const eIdx = dayIndex.get(clampedTo);
                if (sIdx === undefined || eIdx === undefined) return null;
                const meta = ABSENCE_TYPE_META[entry.type];
                const pending = entry.status === 'pending';
                const span = eIdx - sIdx + 1;
                const half = entry.halfDayStart || entry.halfDayEnd;
                return (
                  <Tooltip key={entry.id}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'z-10 mx-px h-5 min-w-0 self-center truncate rounded-md border px-1 text-left text-[10px] font-medium leading-4',
                          meta.band,
                          pending && 'border-dashed opacity-75',
                        )}
                        style={{ gridColumn: `${sIdx + 2} / span ${span}`, gridRow: 1 }}
                        aria-label={`${row.firstName} ${row.lastName}: ${absenceTypeLabel(entry.type)}, ${formatDateRange(entry.dateFrom, entry.dateTo)}`}
                      >
                        {span >= (compact ? 4 : 2) ? absenceTypeShort(entry.type) : ''}
                        {half && span >= 2 ? ' ½' : ''}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-64">
                      <p className="font-semibold">{row.firstName} {row.lastName}</p>
                      <p>
                        {absenceTypeLabel(entry.type)} · {absenceStatusLabel(entry.status)}
                      </p>
                      <p>{formatDateRange(entry.dateFrom, entry.dateTo)}{half ? ' (½)' : ''}</p>
                      {isHr && entry.reason && <p className="italic">{entry.reason}</p>}
                      {isHr && entry.sickLeave?.letterCode && (
                        <p>{t('hr.sick.code')}: {entry.sickLeave.letterCode}</p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {rows.length > visibleCount && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 w-full"
          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
        >
          {t('hr.grid.showMore', { count: Math.min(PAGE_SIZE, rows.length - visibleCount) })}
        </Button>
      )}

      {legendTypes.length > 0 && (
        <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="bg-muted size-3 rounded-sm border" />
            {t('hr.calendar.legendWeekend')}
          </span>
          {legendTypes.map((type) => (
            <Fragment key={type}>
              <span className="flex items-center gap-1.5">
                <span className={cn('size-3 rounded-sm border', ABSENCE_TYPE_META[type].band)} />
                {absenceTypeLabel(type)}
              </span>
            </Fragment>
          ))}
          <span className="flex items-center gap-1.5">
            <span className="size-3 rounded-sm border border-dashed" />
            {t('hr.calendar.legendPending')}
          </span>
        </div>
      )}
    </TooltipProvider>
  );
}
